import { useState, useEffect, useCallback, useRef } from 'react';
/* eslint-disable */
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Chip,
    Button
} from '@mui/material';
import { CloseOutlined, ReceiptLongOutlined, EditOutlined, AddOutlined } from '@mui/icons-material';
import { salaryList } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { format } from 'date-fns';

const SalaryListModal = ({ open, handleClose, employee, onOpenSalaryModal, onOpenEditSalaryModal, isSalaryModalOpen, onRefresh }) => {
    const [salaryData, setSalaryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const fetchSalaryList = useCallback(async () => {
        if (!employee?.id) return;

        setLoading(true);
        try {
            const payload = {
                employee_id: employee.id,
                pageNumber: page + 1,
                length: rowsPerPage
            };

            const response = await salaryList(payload);
            if (response?.data?.success) {
                setSalaryData(response.data.data || []);
                setTotalCount(response.data.totalRecordsWithFilter || response.data.data?.length || 0);
            } else {
                setSalaryData([]);
                setTotalCount(0);
            }
        } catch (error) {
            handleCatchError(error);
            setSalaryData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [employee?.id, page, rowsPerPage]);

    const prevIsSalaryModalOpen = useRef(isSalaryModalOpen);

    useEffect(() => {
        // If payment modal was open and now it's closed, refresh the list
        if (open && prevIsSalaryModalOpen.current && !isSalaryModalOpen) {
            fetchSalaryList();
        }
        prevIsSalaryModalOpen.current = isSalaryModalOpen;
    }, [isSalaryModalOpen, open, fetchSalaryList]);

    useEffect(() => {
        if (open) {
            fetchSalaryList();
        }
    }, [open, fetchSalaryList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ p: 0, pb: 0 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #1e3a8a 0%, #1976d2 100%)',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#fff'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,255,255,0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <ReceiptLongOutlined sx={{ color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography color="#fff" variant="h6" fontWeight={700} lineHeight={1.2}>
                                Salary History
                            </Typography>
                            {employee && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    {employee.name} • {employee.id}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddOutlined />}
                            onClick={() => onOpenSalaryModal(employee)}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: '#fff',
                                fontWeight: 700,
                                textTransform: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            Pay Salary
                        </Button>
                        <IconButton
                            size="small"
                            onClick={handleClose}
                            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                        >
                            <CloseOutlined fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {loading && salaryData.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {['Month', 'Year', 'Amount Paid', 'Role', 'Payment Date', 'Actions'].map((h) => (
                                            <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: 'text.secondary' }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {salaryData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                                <Typography color="text.secondary">No salary records found for this employee.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        salaryData.map((row) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {row.month_name || row.month}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{row.year}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color="success.main">
                                                        ₹{Number(row.amount || 0).toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={row.role || 'NA'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem', height: 20, textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {row.created_at ? format(new Date(row.created_at), 'dd MMM yyyy, hh:mm a') : 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onOpenEditSalaryModal(employee, row)}
                                                        sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.lighter' } }}
                                                    >
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                        />
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

SalaryListModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object,
    onOpenSalaryModal: PropTypes.func,
    onOpenEditSalaryModal: PropTypes.func,
    isSalaryModalOpen: PropTypes.bool,
    onRefresh: PropTypes.func
};

export default SalaryListModal;
